import { useRef, useState, type Dispatch, type PointerEvent as ReactPointerEvent, type SetStateAction } from "react";

/*
 * Drag-to-reorder by a handle, shared by Catálogo → Áreas and a study's analytes. Pointer events,
 * so the tablet's stylus and a finger work, not only a mouse; the handle has `touch-action: none`
 * (`.drag-handle`) so dragging does not scroll the page. The row under the pointer becomes the
 * dragged row's new position. The up/down buttons beside each row are the keyboard path.
 */

export function moveItem<T>(xs: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= xs.length || to >= xs.length) return xs;
  const next = [...xs];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

export function useDragReorder(setOrder: Dispatch<SetStateAction<string[]>>) {
  const rowEls = useRef(new Map<string, HTMLElement>());
  const dragging = useRef<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  const end = () => {
    dragging.current = null;
    setDragId(null);
  };

  /** Ref callback for the row element of `id` (its bounds decide the drop position). */
  const rowRef = (id: string) => (el: HTMLElement | null) => {
    if (el) rowEls.current.set(id, el);
    else rowEls.current.delete(id);
  };

  /** Props for the handle of row `id`. */
  const gripProps = (id: string) => ({
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      dragging.current = id;
      setDragId(id);
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      const moving = dragging.current;
      if (!moving) return;
      setOrder((cur) => {
        const target = cur.findIndex((other) => {
          const r = rowEls.current.get(other)?.getBoundingClientRect();
          return r !== undefined && e.clientY >= r.top && e.clientY < r.bottom;
        });
        return target < 0 ? cur : moveItem(cur, cur.indexOf(moving), target);
      });
    },
    onPointerUp: end,
    onPointerCancel: end,
  });

  /** Move `id` by `delta` places (the up/down buttons). */
  const step = (id: string, delta: number) => setOrder((cur) => moveItem(cur, cur.indexOf(id), cur.indexOf(id) + delta));

  return { dragId, rowRef, gripProps, step };
}
